import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  replaceEqualDeep,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import {
  getWorkspace,
  sendCommands,
  undo as undoRequest,
} from '../../../../service/exams.api';
import { ingestionKeys } from '../../../Exams/shared/keys';
import { mergeReply, normalise, scriptName, withRoster } from './model';
import { applyCommands, CommandRejected } from './commands';
import { CHECK_PAGES_COPY, rejectionMessage } from '../copy';

// useIngestionWorkspace(jobId) — optimistic apply, serial sends, server undo
// (05 §6.4, 90 #43).
//
//   confirmed = the last server state (query ingestionKeys.workspace, normalised)
//   pending   = one entry per user action, applied locally, not yet confirmed
//   view      = pending applied over confirmed
//
// One user action = one request (≤ 50 commands), sent one at a time with the
// confirmed `expectedVersion`; nothing is ever batched across actions. Undo of
// an action that hasn't been sent drops it locally; otherwise it goes through
// the same serial queue as POST /ingestion/{jobId}/undo, and the server pops
// its top group. The UI never computes inverses for the server: a failed send
// is rolled back by dropping its entry (the view is recomputed from confirmed).

export const RETRY_DELAYS = Object.freeze([1000, 4000, 15000]);
export const MAX_COMMANDS_PER_ACTION = 50;
const MAX_CONFLICT_REPLAYS = 3;

const isTransient = (error) =>
  !error?.status ||
  error.status >= 500 ||
  error.code === 'NETWORK' ||
  error.code === 'TRY_AGAIN';

const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

/**
 * Keep the cached workspace when a fetch resolves with an older version than
 * a command reply merged meanwhile (the job version only grows): a fetch that
 * started before the reply never puts the view back.
 */
export const keepNewerWorkspace = (oldData, newData) => {
  if (
    oldData &&
    newData &&
    Number.isFinite(oldData.version) &&
    Number.isFinite(newData.version) &&
    newData.version < oldData.version
  ) {
    return oldData;
  }
  return replaceEqualDeep(oldData, newData);
};

/** The workspace query, shared by every reader of the same job. */
export const workspaceQueryOptions = (jobId) => ({
  queryKey: ingestionKeys.workspace(jobId),
  queryFn: ({ signal }) =>
    getWorkspace(jobId, { signal }).then((view) => normalise(view)),
  structuralSharing: keepNewerWorkspace,
});

export function useWorkspaceQuery(jobId, { enabled = true } = {}) {
  return useQuery({
    ...workspaceQueryOptions(jobId),
    enabled: Boolean(jobId) && enabled,
    refetchOnWindowFocus: false,
  });
}

/** "added" / "moved from {name}" annotations one action makes: {pageId: mark}. */
export const annotate = (base, commands) => {
  const marks = {};
  commands.forEach((command) => {
    if (command.type !== 'MOVE_PAGE' || !command.toScriptId) return;
    const page = base?.pages?.[command.pageId];
    if (!page) return;
    const from = page.scriptId ? base.scripts[page.scriptId] : null;
    if (page.scriptId === command.toScriptId) return;
    marks[command.pageId] = from
      ? { added: false, movedFrom: scriptName(from) }
      : { added: true, movedFrom: null };
  });
  return marks;
};

/** The page labels: every live action's marks, later actions winning. */
const mergeMarks = (log) =>
  log.reduce((all, item) => Object.assign(all, item.marks), {});

/**
 * @param {string} jobId
 * @param {object} [opts]
 * @param {boolean} [opts.enabled]
 * @param {Array} [opts.roster]          roster entries (GET …/roster), for validation and names
 * @param {(reply, entry) => void} [opts.onReply]   every accepted reply (credit post gate)
 * @param {(message: string) => void} [opts.notify] toasts ("Undone: …", rejections)
 * @param {number[]} [opts.retryDelays]
 */
export default function useIngestionWorkspace(
  jobId,
  {
    enabled = true,
    roster = null,
    onReply,
    notify,
    retryDelays = RETRY_DELAYS,
  } = {}
) {
  const queryClient = useQueryClient();
  const query = useWorkspaceQuery(jobId, { enabled });
  const [pending, setPendingState] = useState([]);
  const [sendError, setSendError] = useState(null);
  const [saving, setSaving] = useState(false);
  const [annotations, setAnnotations] = useState({});
  // Label marks per action ({clientSeq, marks}) and the actions the server
  // confirmed this session, newest last: an undo (local or server) takes its
  // action's marks with it, so "moved from …" never outlives the move.
  const markLog = useRef([]);
  const confirmedSeqs = useRef([]);
  const pendingRef = useRef([]);
  const running = useRef(false);
  const paused = useRef(false);
  const inFlight = useRef(null);
  const attempts = useRef(0);
  const conflicts = useRef(0);
  const seq = useRef(0);
  const mounted = useRef(true);
  const opts = useRef({ onReply, notify, retryDelays });
  opts.current = { onReply, notify, retryDelays };

  useEffect(
    () => () => {
      mounted.current = false;
    },
    []
  );

  const setPending = useCallback((next) => {
    pendingRef.current = next;
    if (mounted.current) setPendingState(next);
  }, []);

  const confirmed = query.data;
  const view = useMemo(() => {
    let ws = withRoster(confirmed, roster);
    if (!ws) return ws;
    pending.forEach((entry) => {
      if (entry.kind !== 'commands') return;
      try {
        ws = applyCommands(ws, entry.commands);
      } catch (error) {
        if (!(error instanceof CommandRejected)) throw error;
      }
    });
    return ws;
  }, [confirmed, pending, roster]);
  const viewRef = useRef(view);
  viewRef.current = view;

  const say = useCallback((message) => {
    if (message) opts.current.notify?.(message);
  }, []);

  const currentConfirmed = useCallback(
    () => queryClient.getQueryData(ingestionKeys.workspace(jobId)),
    [queryClient, jobId]
  );

  const dropEntry = useCallback(
    (clientSeq) =>
      setPending(
        pendingRef.current.filter((entry) => entry.clientSeq !== clientSeq)
      ),
    [setPending]
  );

  const setMarkLog = useCallback((next) => {
    markLog.current = next;
    if (mounted.current) setAnnotations(mergeMarks(next));
  }, []);

  /** An action that will never be on the server (rolled back or undone): forget its marks. */
  const forgetMarks = useCallback(
    (clientSeq) => {
      if (!markLog.current.some((item) => item.clientSeq === clientSeq)) return;
      setMarkLog(
        markLog.current.filter((item) => item.clientSeq !== clientSeq)
      );
    },
    [setMarkLog]
  );

  /** Refetch, then re-apply what is still pending; drop what now fails. */
  const replay = useCallback(async () => {
    let fresh;
    try {
      fresh = await queryClient.fetchQuery({
        ...workspaceQueryOptions(jobId),
        staleTime: 0,
      });
    } catch {
      return false;
    }
    let ws = withRoster(fresh, roster);
    let dropped = 0;
    const kept = [];
    pendingRef.current.forEach((entry) => {
      if (entry.kind !== 'commands') {
        kept.push(entry);
        return;
      }
      try {
        ws = applyCommands(ws, entry.commands);
        kept.push(entry);
      } catch (error) {
        if (!(error instanceof CommandRejected)) throw error;
        dropped += 1;
        forgetMarks(entry.clientSeq);
      }
    });
    setPending(kept);
    if (dropped > 0) say(CHECK_PAGES_COPY.changedElsewhere);
    return true;
  }, [queryClient, jobId, roster, setPending, say, forgetMarks]);

  const invalidateTexts = useCallback(
    (reply) => {
      const ids = [
        ...(reply?.changed?.scripts || []).map((script) => script.scriptId),
        ...(reply?.changed?.removedScriptIds || []),
      ];
      ids.forEach((scriptId) =>
        queryClient.invalidateQueries({
          queryKey: ingestionKeys.scriptText(jobId, scriptId),
        })
      );
      (reply?.changed?.pages || []).forEach((page) =>
        queryClient.invalidateQueries({
          queryKey: ingestionKeys.page(jobId, page.pageId),
        })
      );
    },
    [queryClient, jobId]
  );

  /** Send one entry. Resolves 'next' (continue the loop) or 'stop'. */
  const sendEntry = useCallback(
    async (entry) => {
      const version = currentConfirmed()?.version ?? 0;
      try {
        const reply =
          entry.kind === 'undo'
            ? await undoRequest(jobId, version)
            : await sendCommands(jobId, version, entry.commands, entry.label);
        // A refetch still in flight (the status poll invalidates on every
        // ingestionVersion move) would land over this reply when it resolves:
        // cancel it, merge the reply, then fetch again so whatever that
        // refetch was for (segment results, …) still arrives.
        const workspaceKey = ingestionKeys.workspace(jobId);
        const refetching =
          queryClient.isFetching({ queryKey: workspaceKey }) > 0;
        if (refetching)
          await queryClient.cancelQueries({ queryKey: workspaceKey });
        queryClient.setQueryData(workspaceKey, (ws) => mergeReply(ws, reply));
        if (refetching)
          queryClient.invalidateQueries({ queryKey: workspaceKey });
        dropEntry(entry.clientSeq);
        if (entry.kind === 'undo') {
          const undone = confirmedSeqs.current.pop();
          if (undone !== undefined) forgetMarks(undone);
        } else {
          confirmedSeqs.current.push(entry.clientSeq);
        }
        attempts.current = 0;
        conflicts.current = 0;
        if (mounted.current) setSendError(null);
        invalidateTexts(reply);
        if (reply?.undone) {
          say(CHECK_PAGES_COPY.undone(reply.undone.label || entry.label));
        }
        opts.current.onReply?.(reply, entry);
        return 'next';
      } catch (error) {
        if (error?.code === 'UNAUTHENTICATED') {
          paused.current = true;
          return 'stop';
        }
        if (error?.code === 'VERSION_CONFLICT') {
          conflicts.current += 1;
          if (conflicts.current <= MAX_CONFLICT_REPLAYS && (await replay())) {
            return 'next';
          }
          dropEntry(entry.clientSeq);
          forgetMarks(entry.clientSeq);
          conflicts.current = 0;
          say(CHECK_PAGES_COPY.changedElsewhere);
          queryClient.invalidateQueries({
            queryKey: ingestionKeys.workspace(jobId),
          });
          return 'next';
        }
        if (isTransient(error)) {
          attempts.current += 1;
          const delays = opts.current.retryDelays;
          if (attempts.current <= delays.length) {
            if (mounted.current) {
              setSendError({
                message: CHECK_PAGES_COPY.notSaved,
                retrying: true,
              });
            }
            await sleep(delays[attempts.current - 1]);
            return mounted.current ? 'next' : 'stop';
          }
          paused.current = true;
          if (mounted.current) {
            setSendError({
              message: CHECK_PAGES_COPY.notSaved,
              retrying: false,
            });
          }
          return 'stop';
        }
        // A refusal (422 COMMAND_INVALID, 409 NOTHING_TO_UNDO, INGESTION_BUSY,
        // INGESTION_COMMITTED, …): roll the action back and reload.
        dropEntry(entry.clientSeq);
        forgetMarks(entry.clientSeq);
        attempts.current = 0;
        if (mounted.current) setSendError(null);
        say(
          error?.code === 'COMMAND_INVALID'
            ? rejectionMessage(error?.details?.reason, error?.message)
            : error?.message || CHECK_PAGES_COPY.refused
        );
        queryClient.invalidateQueries({
          queryKey: ingestionKeys.workspace(jobId),
        });
        return 'next';
      }
    },
    [
      jobId,
      queryClient,
      currentConfirmed,
      dropEntry,
      forgetMarks,
      invalidateTexts,
      replay,
      say,
    ]
  );

  const run = useCallback(async () => {
    if (running.current || paused.current) return;
    running.current = true;
    if (mounted.current) setSaving(true);
    try {
      while (pendingRef.current.length > 0 && !paused.current) {
        const entry = pendingRef.current[0];
        inFlight.current = entry.clientSeq;
        const result = await sendEntry(entry);
        inFlight.current = null;
        if (result === 'stop') break;
      }
    } finally {
      inFlight.current = null;
      running.current = false;
      if (mounted.current) setSaving(false);
    }
  }, [sendEntry]);

  /** One user action: ≤ 50 commands in one request. Returns {ok, reason?}. */
  const dispatch = useCallback(
    (label, commands) => {
      const list = Array.isArray(commands) ? commands : [commands];
      if (list.length === 0) return { ok: false, reason: 'EMPTY' };
      if (list.length > MAX_COMMANDS_PER_ACTION) {
        say(CHECK_PAGES_COPY.tooMany);
        return { ok: false, reason: 'TOO_MANY_COMMANDS' };
      }
      const base = viewRef.current;
      if (!base) return { ok: false, reason: 'NOT_LOADED' };
      try {
        applyCommands(base, list);
      } catch (error) {
        if (!(error instanceof CommandRejected)) throw error;
        say(rejectionMessage(error.reason));
        return { ok: false, reason: error.reason };
      }
      seq.current += 1;
      const marks = annotate(base, list);
      if (Object.keys(marks).length > 0) {
        setMarkLog([...markLog.current, { clientSeq: seq.current, marks }]);
      }
      setPending([
        ...pendingRef.current,
        {
          clientSeq: seq.current,
          kind: 'commands',
          label: label || null,
          commands: list,
        },
      ]);
      run();
      return { ok: true };
    },
    [run, say, setPending, setMarkLog]
  );

  const pendingUndos = pending.filter((entry) => entry.kind === 'undo').length;
  const pendingActions = pending.filter(
    (entry) => entry.kind === 'commands'
  ).length;
  const canUndo =
    pendingActions > 0 || (confirmed?.undoDepth ?? 0) - pendingUndos > 0;

  const undo = useCallback(() => {
    const list = pendingRef.current;
    const last = list[list.length - 1];
    if (
      last &&
      last.kind === 'commands' &&
      last.clientSeq !== inFlight.current
    ) {
      setPending(list.slice(0, -1));
      forgetMarks(last.clientSeq);
      say(CHECK_PAGES_COPY.undone(last.label));
      return;
    }
    if (!canUndo) return;
    seq.current += 1;
    setPending([
      ...list,
      { clientSeq: seq.current, kind: 'undo', label: null },
    ]);
    run();
  }, [canUndo, run, say, setPending, forgetMarks]);

  /** "Changes not saved yet · Retry". */
  const retry = useCallback(() => {
    paused.current = false;
    attempts.current = 0;
    setSendError(null);
    run();
  }, [run]);

  /** Resolves true once everything is sent, false if sending is stuck. */
  const whenIdle = useCallback(
    () =>
      new Promise((resolve) => {
        const check = () => {
          if (paused.current) {
            resolve(false);
            return;
          }
          if (pendingRef.current.length === 0 && !running.current) {
            resolve(true);
            return;
          }
          setTimeout(check, 50);
        };
        check();
      }),
    []
  );

  return {
    ws: view,
    confirmed,
    isLoading: query.isLoading,
    isError: query.isError,
    error: query.error,
    refetch: query.refetch,
    dispatch,
    undo,
    canUndo,
    pendingCount: pending.length,
    saving,
    sendError,
    retry,
    whenIdle,
    annotations,
  };
}
